import type { Locale, Messages } from "./index";

/**
 * DukaanBook's words (DukaanBook-Design.docx, section 7): shared words stay
 * shared; only these change. "Pharmacy" becomes "shop" (dukaan), "medicine"
 * becomes "product" (alaab).
 *
 * The Somali here is checked by the owner before release, as with FarmashiBook.
 */

type Overlay = Partial<Omit<Messages, "errors">> & { errors?: Partial<Messages["errors"]> };

const overlayEn: Overlay = {
  appName: "DukaanBook",
  updateTitle: "Update DukaanBook",
  appUpdated: "DukaanBook has been updated. Reload the page to carry on.",
  webTagline: "Your shop's stock, sales and credit, on the computer and the phone.",
  webFeatureStock: "Products and stock",
  welcomeTagline: "Run your shop from your phone",
  createPharmacy: "Set up your shop",
  pharmacyName: "Shop name",
  pharmacyPhoneHint: "So the shop can be reached. To sign in with it too, confirm it later in Profile.",
  pharmacyAccount: "Shop account",
  settingsPharmacy: "Shop",
  pharmacyPhone: "Shop phone",
  medicine: "Product",
  searchSales: "Name, phone, receipt or product",
  deletePharmacy: "Delete shop account",
  deleteStock: "Every product and its stock",
  cancelRecordedConfirm: "Are you sure you want to cancel this sale? The products go back into stock.",
  cancelWhyHint: "The products go back into stock. The sale stays in the history, marked Cancelled.",
  planGraceStaff: "The shop's plan has ended. Please tell the owner.",
  payStep2: "Send your shop's name on WhatsApp.",
  pharmacyNeedsInternet: "Shop account needs internet.",
  usernameHint: "Unique across every shop, so pick from the suggestions if one is taken.",
  leavePharmacy: "Leave this shop",
  medicineCount: (n: number) => `${n} ${n === 1 ? "product" : "products"}`,
  addMedicineToSend: "Add a product",
  searchThisBranch: "Search this branch's products",
  whichOfYours: "Which of your products is this?",
  addAsNew: "Add as a new product",
  notWholeHere: (unit: string) => `That is not a whole number of ${unit}, which your product is counted in.`,
  addMedicineLink: "+ Add product",
  switchBranchHint: "Everything you see — products, stock, sales, credit — is for the branch you pick.",
  branchesHint: "Each branch is run like a shop of its own: its own products, prices, stock, suppliers and customers.",
  branchAdded: (name: string) => `${name} is added. It starts empty: switch to it from Home to record its products.`,
  costOfMedicines: "What the products cost",
  attentionBelowZero: "Products below zero",
  medicinesTotal: "Products",
  medicinesWord: (n: number): string => (n === 1 ? "product" : "products"),
  // Products (2026-10-27): no medicine list, one name, the shop's own packs.
  newMedicine: "New product",
  addNewMedicineNamed: (q: string) => `Add "${q}" as a new product`,
  noMedicinesYet: "No products yet.",
  webPickToAddStock: "Choose a product to add its stock",
  searchProducts: "Search by name",
  productNameHint: "The name you call it, such as Bariis or Coca-Cola.",
  archiveProductHint:
    "It stops appearing in search and at the till. Nothing already sold is lost. Bring it back from Settings → Archived products.",
  archivedMedicines: "Archived products",
  archivedMedicinesHint: "Bring back a product you archived",
  archivedListNote:
    "Products archived in this branch. Bring one back and it is in Products and at the till again, with the stock it had.",
  noArchived: "No archived products in this branch.",
  nothingDead: (days: number) => `Every product with stock has sold in the last ${days} days.`,
  nothingOutOfStock: (n: number) => `Every product has ${n} or more left.`,
  sizeLabelHint: "Such as kiilo, packet, carton",
  newMedicineHint: "Only what this sale needs. Record its stock and cost later in Products.",
  addAnotherMedicine: "+ Add another product",
  pickMedicine: "Type or pick a product",
  removeMedicine: "Remove this product",
  webSelectToChange: "Select the customer or a product to change it.",
  /** The smallest amount ever sold (the owner's list, 2026-10-27). */
  countedInOptions: ["piece", "tumun", "⅛ litre", "packet", "bottle", "can"],
  /** Smallest to biggest: the order packs are shown in. */
  packOptions: ["rubac", "nus kiilo", "kiilo", "litre", "packet", "carton", "sack", "jerrycan"],
  /** The shop's Somali units stay as they are said: 3 kiilo, not 3 kiilos. */
  plural: (word: string, n: number) =>
    n === 1 || SOMALI_UNITS.has(word.trim().toLowerCase())
      ? word
      : /(s|x|z|ch|sh)$/i.test(word)
        ? `${word}es`
        : `${word}s`,
  errors: {
    wrong_app: "This account belongs to FarmashiBook. Sign in there.",
    pharmacy_deleted: "This shop was deleted. For 30 days after that, support can bring it back.",
    contact_phone_required: "Enter the shop's phone number.",
    size_exists: "This product already has a size holding that many.",
    not_whole_units: "That does not make whole units of your product.",
    different_unit: "That product is counted in a unit that does not match.",
    every_line: "Say something for every product.",
    count_needs_stock: "There is no stock of it yet. Record what arrived on Add product.",
  },
};

/** Units a shop says in Somali whatever the app's language: no English plural. */
const SOMALI_UNITS = new Set(["tumun", "rubac", "nus kiilo", "kiilo", "xabo", "baakad", "dhalo", "gasac", "kiish", "jirgaan"]);

const overlaySo: Overlay = {
  appName: "DukaanBook",
  updateTitle: "Cusbooneysii DukaanBook",
  appUpdated: "DukaanBook waa la cusboonaysiiyay. Dib u soo rar bogga si aad u sii wadato.",
  webTagline: "Kaydka, iibka iyo deynta dukaankaaga, ka maamul kombiyuutarka iyo taleefanka.",
  webFeatureStock: "Alaabta iyo kaydka",
  welcomeTagline: "Dukaankaaga ka maamul taleefankaaga",
  createPharmacy: "Diyaari dukaankaaga",
  pharmacyName: "Magaca dukaanka",
  pharmacyPhoneHint:
    "Si dukaanka loola xiriiro. Si aad sidoo kale ugu isticmaasho soo galitaanka, mar dambe ku xaqiiji Profile-ka.",
  pharmacyAccount: "Akoonka dukaanka",
  settingsPharmacy: "Dukaanka",
  pharmacyPhone: "Taleefanka dukaanka",
  medicine: "Alaabta",
  searchSales: "Magac, taleefan, rasiidh ama alaab",
  deletePharmacy: "Tirtir akoonka dukaanka",
  deleteStock: "Alaab kasta iyo kaydkeeda",
  deleteAfterDays: (phone: string) =>
    `30 maalmood kadib si buuxda ayaa loo tirtirayaa. Ilaa markaas, La xariir ${phone} si loo soo celiyo xogtaada dukaanka iyo akoonkaaga.`,
  cancelRecordedConfirm: "Ma hubtaa inaad joojinayso iibkan? Alaabtu waxay ku noqonaysaa kaydka.",
  cancelWhyHint: "Alaabtu waxay ku noqonaysaa kaydka. Iibku wuxuu ku jirayaa taariikhda, isagoo calaamadsan La joojiyay.",
  planGraceStaff: "Qorshaha isticmaalka dukaanka waa dhammaaday. Fadlan u sheeg milkiilaha.",
  payStep2: "Magaca dukaankaaga ku soo dir WhatsApp.",
  pharmacyNeedsInternet: "Akoonka dukaanku wuxuu u baahan yahay internet.",
  usernameHint: "Waa mid gaar ah dukaan kasta, haddii la qaatay, dooro mid ka mid kuwa aan kusoo jeediney ee hoos ka muuqda.",
  leavePharmacy: "Ka bax dukaankan",
  medicineCount: (n: number) => `${n} alaab`,
  addMedicineToSend: "Ku dar alaab",
  searchThisBranch: "Ka raadi alaabta laantan",
  whichOfYours: "Alaabtaada midkee waa tan?",
  addAsNew: "Ku dar alaab cusub",
  notWholeHere: (unit: string) => `Taasi ma aha tiro dhan oo ${unit} ah, oo alaabtaadu ku tirsan tahay.`,
  addMedicineLink: "+ Ku dar alaab",
  switchBranchHint: "Wax kasta oo aad aragto — alaabta, kaydka, iibka, deynta — waa laanta aad dooratay.",
  branchesHint:
    "Laan kasta waxay u shaqaysaa sidii dukaan u gaar ah: alaabteeda, qiimahooda, kaydkeeda, alaab-qeybiyeyaasheeda iyo macaamiisheeda.",
  branchAdded: (name: string) =>
    `${name} waa lagu daray. Waxay ku bilaabataa madhan: ka beddel Bogga Hore si aad u diiwaangeliso alaabteeda.`,
  costOfMedicines: "Qiimaha alaabta",
  attentionBelowZero: "Alaab ka hooseysa eber",
  medicinesTotal: "Alaabta",
  medicinesWord: (_n: number): string => "alaab",
  newMedicine: "Alaab cusub",
  addNewMedicineNamed: (q: string) => `Ku dar "${q}" alaab cusub ahaan`,
  noMedicinesYet: "Weli alaab ma jirto.",
  webPickToAddStock: "Dooro alaab si aad ugu darto kaydkeeda",
  searchProducts: "Ku raadi magaca",
  productNameHint: "Magaca aad u taqaanid, sida Bariis ama Coca-Cola.",
  archiveProductHint:
    "Kama muuqan doonto raadinta iyo goobta iibka. Wixii hore loo iibiyay waa sii jiraan. Dib uga soo celi Dejinta → Alaabta la kaydiyay.",
  archivedMedicines: "Alaabta la kaydiyay",
  archivedMedicinesHint: "Dib u soo celi alaab aad kaydisay",
  archivedListNote:
    "Alaabta laantan lagu kaydiyay. Mid dib u soo celi, waxay ku soo noqonaysaa Alaabta iyo goobta iibka, oo leh kaydkii ay lahayd.",
  noArchived: "Laantan ma laha alaab la kaydiyay.",
  nothingDead: (days: number) => `Alaab kasta oo kayd leh waa la iibiyay ${days}-kii maalmood ee la soo dhaafay.`,
  nothingOutOfStock: (n: number) => `Alaab kasta waxaa ka haray ${n} ama ka badan.`,
  sizeLabelHint: "Sida kiilo, baakad, kartoon",
  newMedicineHint: "Kaliya waxa iibkan u baahan yahay. Kaydkeeda iyo qiimaha iibsiga hadhow ku qor Alaabta.",
  addAnotherMedicine: "+ Ku dar alaab kale",
  pickMedicine: "Qor ama dooro alaab",
  removeMedicine: "Ka saar alaabtan",
  webSelectToChange: "Dooro macmiilka ama alaab si aad u beddesho.",
  countedInOptions: ["xabo", "tumun", "⅛ litir", "baakad", "dhalo", "gasac"],
  packOptions: ["rubac", "nus kiilo", "kiilo", "litir", "baakad", "kartoon", "kiish", "jirgaan"],
  errors: {
    wrong_app: "Xisaabtani waxay ka tirsan tahay FarmashiBook. Halkaas ka gal.",
    pharmacy_deleted: "Dukaankan waa la tirtiray. 30 maalmood gudahood kadib tirtirka, taageeradu way soo celin kartaa.",
    contact_phone_required: "Geli lambarka taleefanka dukaanka.",
    size_exists: "Alaabtan horey ayey u leedahay cabbir intaas haya.",
    not_whole_units: "Taasi ma samaynayso cutubyo dhammaystiran oo alaabtaada ah.",
    different_unit: "Alaabtaas waxaa lagu tiriyaa cutub aan u dhigmin.",
    every_line: "Wax ka sheeg alaab kasta.",
    count_needs_stock: "Weli kayd ma laha. Wixii yimid ku qor Ku dar alaab.",
  },
};

const OVERLAYS: Record<Locale, Overlay> = { en: overlayEn, so: overlaySo };

/** "Wrong medicine" is "Wrong product" in a shop (agreed 2026-10-23). */
const WRONG_PRODUCT: Record<Locale, string> = { en: "Wrong product", so: "Alaab khaldan" };

/** A shop's product with its expiry switched on, saved without a date. */
const EXPIRY_REQUIRED: Record<Locale, string> = {
  en: "This product has an expiry date. Enter it.",
  so: "Alaabtan waxay leedahay taariikh dhicitaan. Qor.",
};

/** "Damaged" is "Spilled or damaged" in a shop: loose goods spill (agreed 2026-10-25). */
const SPILLED: Record<Locale, string> = { en: "Spilled or damaged", so: "Wuu daatay ama burburay" };

/** The staff activity lines that name a medicine, about a product instead. */
const ACTIVITY: Record<Locale, Partial<Messages["activity"]>> = {
  en: {
    medicine_added: (product: string) => `Added product: ${product}`,
    medicine_changed: (product: string) => `Changed product: ${product}`,
    medicine_archived: (product: string) => `Archived product: ${product}`,
  },
  so: {
    medicine_added: (product: string) => `Alaab la daray: ${product}`,
    medicine_changed: (product: string) => `Alaab la beddelay: ${product}`,
    medicine_archived: (product: string) => `Alaab la kaydiyay: ${product}`,
  },
};

/** The shared catalogue with DukaanBook's words on top. */
export function withDukaanWords(base: Messages, locale: Locale): Messages {
  const overlay = OVERLAYS[locale];
  return {
    ...base,
    ...overlay,
    errors: { ...base.errors, ...overlay.errors },
    saleReason: { ...base.saleReason, wrong_medicine: WRONG_PRODUCT[locale] },
    adjustReason: { ...base.adjustReason, damaged: SPILLED[locale] },
    lotProblem: { ...base.lotProblem, expiry_required: EXPIRY_REQUIRED[locale] },
    activity: { ...base.activity, ...ACTIVITY[locale] },
  } as Messages;
}

/* --------------------------------------------------- shop-only words */

/**
 * Words only DukaanBook's screens use: its Product page and selling by
 * money. Everything else is the shared catalogue with the overlay above.
 */
const shopEn = {
  /** Shops ask for expiry only when switched on (decision D7). */
  hasExpiry: "Has an expiry date",
  hasExpiryHint: "For juice, milk, sweets and similar.",
  countStock: "Count the stock",
  countNow: "On the shelf now",
  countWhy: "Why is it different?",
  countSave: "Save the count",
  countDifference: (text: string) => `Difference: ${text}`,
  prices: "Prices",
  /** Selling by money: "$1 of sugar" (2026-10-27). */
  byMoney: "By money",
  byMoneyHint: "Type the money the customer gives.",
  aboutQty: (text: string) => `about ${text}`,
  moneyNeedsPrice: (unit: string) => `Give the ${unit} a selling price first, in Products.`,
};

export type ShopWords = typeof shopEn;

const shopSo: ShopWords = {
  hasExpiry: "Waxay leedahay taariikh dhicitaan",
  hasExpiryHint: "Casiir, caano, macmacaan iyo wax la mid ah.",
  countStock: "Tiri kaydka",
  countNow: "Hadda khaanadda saaran",
  countWhy: "Maxay u kala duwan yihiin?",
  countSave: "Kaydi tirinta",
  countDifference: (text: string) => `Farqiga: ${text}`,
  prices: "Qiimaha",
  byMoney: "Lacag ahaan",
  byMoneyHint: "Qor lacagta macmiilku bixinayo.",
  aboutQty: (text: string) => `qiyaastii ${text}`,
  moneyNeedsPrice: (unit: string) => `Marka hore ${unit} sii qiime iib, Alaabta dhexdeeda.`,
};

const SHOP: Record<Locale, ShopWords> = { en: shopEn, so: shopSo };

export function shopWordsFor(locale: Locale): ShopWords {
  return SHOP[locale];
}
